import { Button, DesktopModal, Input, Modal, Select, Switch, TextArea } from '@byecode/ui'
import type { TypeInstanceMap } from '@lighthouse/core'
import type { ExtractParams } from '@lighthouse/shared'
import { ErrorMessage } from '@lighthouse/shared'
import { nanoid } from '@lighthouse/tools'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'

import * as SC from './ParameterModalStyles'

type Props = {
    isOpen: boolean
    onclose: () => void
    onOk: (params: ExtractParams, type: 'add' | 'update') => void
    currentIndex: number
    list: ExtractParams[]
    options: { label: string; value: string }[]
}

export const ParameterModal = ({ isOpen, onclose, onOk, currentIndex, list, options }: Props) => {
    const [loading, setLoading] = useState(false)

    const currentItem = useMemo(
        () =>
            currentIndex >= 0
                ? list[currentIndex]
                : {
                      name: '',
                      innerType: 'TEXT' as const,
                      description: '',
                      required: false,
                      id: nanoid()
                  },
        [currentIndex, list]
    )

    const {
        control,
        register,
        handleSubmit,
        getValues,
        formState: { errors },
        reset
    } = useForm<ExtractParams>({
        mode: 'onSubmit',
        values: {
            name: currentItem.name,
            innerType: currentItem.innerType,
            description: currentItem.description,
            required: currentItem.required,
            id: currentItem.id
        }
    })

    const handleUpdate = useCallback(
        (params: ExtractParams) => {
            onOk({ ...params, id: nanoid() }, currentIndex === -1 ? 'add' : 'update')
        },
        [currentIndex, onOk]
    )

    useEffect(() => {
        if (!isOpen) {
            reset()
        }
    }, [isOpen, reset, currentItem])

    const handleInviteSubmit = useCallback(
        (ev: React.FormEvent) => {
            ev.preventDefault()
            handleSubmit(handleUpdate)()
        },
        [handleSubmit, handleUpdate]
    )

    return (
        <DesktopModal
            open={isOpen}
            onClose={onclose}
            width={490}
            styles={{
                body: {
                    padding: '0!important'
                },
                header: {
                    borderStyle: 'none',
                    height: '48px'
                }
            }}
        >
            <SC.Container onSubmit={handleInviteSubmit}>
                <SC.Content>
                    <SC.Title>添加参数</SC.Title>
                    <SC.Item>
                        <SC.Text>名称</SC.Text>
                        <ErrorMessage name="name" errors={errors}>
                            <Controller
                                name="name"
                                control={control}
                                rules={{ required: '不能为空' }}
                                render={({ field }) => {
                                    return <Input placeholder="提取参数名称" value={field.value} onChange={field.onChange} />
                                }}
                            />
                        </ErrorMessage>
                    </SC.Item>

                    <SC.Item>
                        <SC.Text>类型</SC.Text>
                        <ErrorMessage name="innerType" errors={errors}>
                            <Controller
                                name="innerType"
                                rules={{ required: '不能为空' }}
                                control={control}
                                render={({ field }) => {
                                    return <Select options={options} placeholder="请选择类型" {...field} />
                                }}
                            />
                        </ErrorMessage>
                    </SC.Item>

                    <SC.Item>
                        <SC.Text>描述</SC.Text>
                        <ErrorMessage name="description" errors={errors}>
                            <Controller
                                name="description"
                                control={control}
                                render={({ field }) => {
                                    return (
                                        <TextArea
                                            style={{ width: '100%' }}
                                            placeholder="请输入描述"
                                            value={field.value}
                                            onChange={field.onChange}
                                        />
                                    )
                                }}
                            />
                        </ErrorMessage>
                    </SC.Item>

                    <SC.Item>
                        <SC.Text>必填</SC.Text>
                        <ErrorMessage name="required" errors={errors}>
                            <Controller
                                name="required"
                                control={control}
                                render={({ field: { value, onChange, ...field } }) => (
                                    <SC.SwitchWrapper>
                                        <Switch checked={value} onChange={onChange} {...field} />
                                        <SC.SwitchText>必填仅作为模型推理的参考，不用于参数输出的强制验证。</SC.SwitchText>
                                    </SC.SwitchWrapper>
                                )}
                            />
                        </ErrorMessage>
                    </SC.Item>
                </SC.Content>
                <SC.Footer>
                    <Button size="lg" style={{ width: 104 }} onClick={onclose}>
                        取消
                    </Button>
                    <Button size="lg" style={{ width: 104 }} loading={loading} type="primary" htmlType="submit">
                        确定
                    </Button>
                </SC.Footer>
            </SC.Container>
        </DesktopModal>
    )
}
